import React, { useState, useEffect } from 'react';
import './styles.css';
import LeftSidebar from './LeftSidebar';
import MainContent from './MainContent';
import RightSidebar from './RightSidebar';
import Lesson, { lessonsData } from './Lesson';
import Profile from './Profile';
import Shop from './Shop';
import Playground from './Playground';
import About from './About';
import Flashcards from './Flashcards';
import LandingPage from './LandingPage';
import AdminDashboard from './AdminDashboard';
import Impressum from './Impressum';
import Privacy from './Privacy';
import Courses from './Courses';
import InstallPWA from './InstallPWA';
import Missions from './Missions';
import { auth, googleProvider, signInWithPopup, signOut, db, doc, setDoc, getDoc, updateDoc, onAuthStateChanged } from './firebase';
import { LogIn, Gift, HeartCrack, Trophy, Award, Download, Tv } from 'lucide-react';
import BackgroundAnimation from './BackgroundAnimation';

const ALL_ACHIEVEMENTS = [
  { id: 'first_win', name: 'Erster Schritt', desc: 'Schließe eine Lektion ab', icon: '🥇' },
  { id: 'streak_3', name: 'Feuer und Flamme', desc: 'Erreiche einen 3-Tage Streak', icon: '🔥' },
  { id: 'perfect', name: 'Scharfschütze', desc: 'Lektion ohne Fehler', icon: '🎯' },
  { id: 'xp_1000', name: 'Wissensdurst', desc: 'Sammle 1000 XP', icon: '⚡' },
  { id: 'night_owl', name: 'Nachteule', desc: 'Lerne spät am Abend', icon: '🦉' },
  { id: 'early_bird', name: 'Früher Vogel', desc: 'Lerne früh am Morgen', icon: '🌅' },
  { id: 'weekend_warrior', name: 'Wochenend-Krieger', desc: 'Lerne am Samstag und Sonntag', icon: '📅' },
  { id: 'marathon', name: 'Marathon', desc: 'Sammle Lernzeit', icon: '⏱️' },
  { id: 'graduate', name: 'Absolvent', desc: 'Schließe einen Abschnitt ab', icon: '🎓' },
  { id: 'survivor', name: 'Überlebenskünstler', desc: 'Lektion mit 1 Herz beendet', icon: '🚑' },
  { id: 'speed_coder', name: 'Speed Coder', desc: 'Lektion unter 1.5 Min', icon: '🏎️' },
  { id: 'memory_pro', name: 'Gedächtnis-Profi', desc: 'Karteikarten wiederholt', icon: '🧠' },
];

function App() {
  const [activeLesson, setActiveLesson] = useState(null);
  const [practiceContent, setPracticeContent] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [xp, setXp] = useState(0);
  const [activeTab, setActiveTab] = useState(() => {
    const path = window.location.pathname.toLowerCase();
    if (path === '/datenschutz') return 'Datenschutz';
    if (path === '/impressum') return 'Impressum';
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'Lernen';
  });
  const [activeCourse, setActiveCourse] = useState('Python');
  const [hearts, setHearts] = useState(5);
  const [streak, setStreak] = useState(0);
  const [lastLessonDate, setLastLessonDate] = useState(null);
  const [streakFreezes, setStreakFreezes] = useState(0);
  const [freezeUsedDates, setFreezeUsedDates] = useState([]);
  const [started, setStarted] = useState(false);
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [ripples, setRipples] = useState([]);
  const [showInstallPWAModal, setShowInstallPWAModal] = useState(false);
  const [showInstallPWAModalIOS, setShowInstallPWAModalIOS] = useState(false);
  
  // Neue States für die neuen Funktionen
  const [gems, setGems] = useState(500);
  const [achievements, setAchievements] = useState([]);
  const [collectedLootboxes, setCollectedLootboxes] = useState([]);
  const [lastHeartUpdate, setLastHeartUpdate] = useState(Date.now());
  const [showLootboxModal, setShowLootboxModal] = useState(false);
  const [showNoHeartsModal, setShowNoHeartsModal] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#1cb0f6');
  const [dailyMissions, setDailyMissions] = useState([]);
  const [lastMissionDate, setLastMissionDate] = useState(null);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [perfectLessonCount, setPerfectLessonCount] = useState(0);
  const [nightOwlCount, setNightOwlCount] = useState(0);
  const [earlyBirdCount, setEarlyBirdCount] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [flashcardsCount, setFlashcardsCount] = useState(0);
  const [certificates, setCertificates] = useState([]);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [earnedCertificate, setEarnedCertificate] = useState(null);
  const [showAdModal, setShowAdModal] = useState(false);
  const [xpBoostEndTime, setXpBoostEndTime] = useState(null);
  const [showCourseDiscoveryModal, setShowCourseDiscoveryModal] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.toLowerCase();
      if (path === '/datenschutz') setActiveTab('Datenschutz');
      else if (path === '/impressum') setActiveTab('Impressum');
      else {
        const params = new URLSearchParams(window.location.search);
        setActiveTab(params.get('tab') || 'Lernen');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const path = window.location.pathname.toLowerCase();
    if (activeTab === 'Datenschutz') {
      if (path !== '/datenschutz') window.history.pushState({}, '', '/datenschutz');
    } else if (activeTab === 'Impressum') {
      if (path !== '/impressum') window.history.pushState({}, '', '/impressum');
    } else {
      if (path === '/datenschutz' || path === '/impressum') {
        window.history.pushState({}, '', '/?tab=' + activeTab);
      } else {
        window.history.replaceState({}, '', '/?tab=' + activeTab);
      }
    }
  }, [activeTab]);

  const generateDailyMissions = () => {
    const templates = [
      { id: 'xp_50', type: 'xp', target: 50, description: 'Verdiene 50 XP', icon: 'Zap' },
      { id: 'time_15', type: 'minutes', target: 15, description: 'Lerne 15 Minuten lang', icon: 'Clock' },
      { id: 'lessons_3', type: 'lessons', target: 3, description: 'Schließe 3 Lektionen ab', icon: 'BookOpen' },
      { id: 'perfect_1', type: 'perfect', target: 1, description: '1 Lektion ohne Fehler', icon: 'Target' },
      { id: 'xp_100', type: 'xp', target: 100, description: 'Verdiene 100 XP', icon: 'Zap' },
      { id: 'time_30', type: 'minutes', target: 30, description: 'Lerne 30 Minuten lang', icon: 'Clock' },
      { id: 'combo_10', type: 'combo', target: 10, description: 'Erreiche eine 10er Combo', icon: 'Target' },
      { id: 'speed_coder', type: 'speed', target: 1, description: 'Lektion unter 2 Min', icon: 'Clock' },
      { id: 'endurance', type: 'lessons', target: 5, description: 'Schließe 5 Lektionen ab', icon: 'BookOpen' },
      { id: 'flashcards_10', type: 'flashcards', target: 10, description: 'Wiederhole 10 Karteikarten', icon: 'RotateCw' },
      { id: 'playground', type: 'playground', target: 1, description: 'Nutze den Playground', icon: 'Terminal' },
      { id: 'early_bird', type: 'early_bird', target: 1, description: 'Lerne vor 12 Uhr mittags', icon: 'Sun' },
      { id: 'night_owl', type: 'night_owl', target: 1, description: 'Lerne nach 20 Uhr', icon: 'Moon' },
    ];
    const shuffled = templates.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3).map(m => ({
        ...m,
        progress: 0,
        completed: false
    }));
  };

  const resetState = () => {
    setXp(0);
    setHearts(5);
    setStreak(0);
    setLastLessonDate(null);
    setStreakFreezes(0);
    setFreezeUsedDates([]);
    setCompletedLessons([]);
    setGems(500);
    setAchievements([]);
    setCollectedLootboxes([]);
    setLastHeartUpdate(Date.now());
    setActiveCourse('Python');
    setDailyMissions(generateDailyMissions());
    setLastMissionDate(new Date().toDateString());
    setDarkMode(false);
    setNotificationsEnabled(false);
    setPerfectLessonCount(0);
    setNightOwlCount(0);
    setEarlyBirdCount(0);
    setTotalTime(0);
    setFlashcardsCount(0);
    setCertificates([]);
    setXpBoostEndTime(null);
  };

  // Authentifizierungs-Status überwachen
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Daten aus Firestore laden
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setXp(data.xp || 0);
          setHearts(data.hearts !== undefined ? data.hearts : 5);
          
          let currentFreezes = data.streakFreezes !== undefined ? data.streakFreezes : 0;
          let currentUsedDates = data.freezeUsedDates ? [...data.freezeUsedDates] : [];
          let currentStreak = data.streak || 0;
          let currentLastLessonDate = data.lastLessonDate || null;

          if (currentLastLessonDate && currentStreak > 0) {
            let lastDate = new Date(currentLastLessonDate);
            lastDate.setHours(0,0,0,0);
            let today = new Date();
            today.setHours(0,0,0,0);
            let yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            let modified = false;
            while (lastDate < yesterday) {
              lastDate.setDate(lastDate.getDate() + 1);
              if (currentFreezes > 0) {
                currentFreezes--;
                currentUsedDates.push(lastDate.toDateString());
                modified = true;
              } else {
                currentStreak = 0;
                modified = true;
                break;
              }
            }

            if (modified && currentStreak > 0) {
               currentLastLessonDate = lastDate.toDateString();
            }
          }
          
          setStreakFreezes(currentFreezes);
          setFreezeUsedDates(currentUsedDates);
          setStreak(currentStreak);
          setLastLessonDate(currentLastLessonDate);
          setCompletedLessons(data.completedLessons || []);
          setDarkMode(data.darkMode || false);
          setNotificationsEnabled(data.notificationsEnabled || false);
          if(data.gems) setGems(data.gems);
          if(data.achievements) setAchievements(data.achievements);
          if(data.collectedLootboxes) setCollectedLootboxes(data.collectedLootboxes);
          setLastHeartUpdate(data.lastHeartUpdate || Date.now());
          if(data.activeCourse) setActiveCourse(data.activeCourse);
          setPerfectLessonCount(data.perfectLessonCount || 0);
          setNightOwlCount(data.nightOwlCount || 0);
          setEarlyBirdCount(data.earlyBirdCount || 0);
          setTotalTime(data.totalTime || 0);
          setFlashcardsCount(data.flashcardsCount || 0);
          setCertificates(data.certificates || []);
          setXpBoostEndTime(data.xpBoostEndTime || null);

          const today = new Date().toDateString();
          if (data.lastMissionDate !== today) {
             setDailyMissions(generateDailyMissions());
             setLastMissionDate(today);
          } else {
             setDailyMissions(data.dailyMissions || generateDailyMissions());
             setLastMissionDate(data.lastMissionDate || today);
          }
        } else {
          // Neuen User in Datenbank anlegen
          const today = new Date().toDateString();
          const newMissions = generateDailyMissions();
          
          // Lokalen State für neuen User setzen
          setXp(0);
          setHearts(5);
          setStreak(0);
          setLastLessonDate(null);
          setStreakFreezes(0);
          setFreezeUsedDates([]);
          setCompletedLessons([]);
          setDarkMode(false);
          setNotificationsEnabled(false);
          setGems(500);
          setAchievements([]);
          setCollectedLootboxes([]);
          setLastHeartUpdate(Date.now());
          setActiveCourse('Python');
          setDailyMissions(newMissions);
          setLastMissionDate(today);
          setPerfectLessonCount(0);
          setNightOwlCount(0);
          setEarlyBirdCount(0);
          setTotalTime(0);
          setFlashcardsCount(0);

          await setDoc(userRef, {
            xp: 0,
            hearts: 5,
            streak: 0,
            lastLessonDate: null,
            streakFreezes: 0,
            freezeUsedDates: [],
            completedLessons: [],
            darkMode: false,
            notificationsEnabled: false,
            gems: 500,
            achievements: [],
            collectedLootboxes: [],
            email: currentUser.email,
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
            lastHeartUpdate: Date.now(),
            activeCourse: 'Python',
            dailyMissions: newMissions,
            lastMissionDate: today,
            perfectLessonCount: 0,
            nightOwlCount: 0,
            earlyBirdCount: 0,
            totalTime: 0,
            flashcardsCount: 0,
            certificates: [],
            xpBoostEndTime: null
          });
        }
        setDataLoaded(true);
        setStarted(true); // Automatisch starten wenn eingeloggt
        setShowLoginModal(false);

        // Einmaliges Popup für andere Kurse anzeigen
        if (!localStorage.getItem('duocode_course_discovery_shown')) {
          localStorage.setItem('duocode_course_discovery_shown', 'true');
          setTimeout(() => setShowCourseDiscoveryModal(true), 2500); // Leicht verzögert anzeigen
        }

        // Automatisch nach Benachrichtigungs-Berechtigung fragen, falls noch nicht geschehen
        if ('Notification' in window && Notification.permission === 'default' && !localStorage.getItem('duocode_notifications_requested')) {
          localStorage.setItem('duocode_notifications_requested', 'true');
          setTimeout(() => {
            Notification.requestPermission().then(permission => {
              if (permission === 'granted') {
                setNotificationsEnabled(true);
              }
            });
          }, 2000); // Kleine Verzögerung, damit die App erst laden kann
        }
      } else {
        setDataLoaded(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Daten speichern wenn sie sich ändern (nur wenn User eingeloggt)
  useEffect(() => {
    if (user && dataLoaded) {
      const saveUserData = async () => {
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, {
          xp: xp,
          hearts: hearts,
          streak: streak,
          lastLessonDate: lastLessonDate,
          streakFreezes: streakFreezes,
          freezeUsedDates: freezeUsedDates,
          completedLessons: completedLessons,
          darkMode: darkMode,
          notificationsEnabled: notificationsEnabled,
          gems: gems,
          achievements: achievements,
          collectedLootboxes: collectedLootboxes,
          lastHeartUpdate: lastHeartUpdate,
          activeCourse: activeCourse,
          dailyMissions: dailyMissions,
          lastMissionDate: lastMissionDate,
          perfectLessonCount: perfectLessonCount,
          nightOwlCount: nightOwlCount,
          earlyBirdCount: earlyBirdCount,
          totalTime: totalTime,
          flashcardsCount: flashcardsCount,
          certificates: certificates,
          xpBoostEndTime: xpBoostEndTime
        });
      };
      // Debounce könnte hier sinnvoll sein, aber für den Anfang reicht direktes Speichern
      saveUserData();
      localStorage.setItem('duocode_streak', streak);
    }
  }, [xp, hearts, streak, lastLessonDate, streakFreezes, freezeUsedDates, completedLessons, user, dataLoaded, darkMode, notificationsEnabled, gems, achievements, collectedLootboxes, lastHeartUpdate, activeCourse, dailyMissions, lastMissionDate, perfectLessonCount, nightOwlCount, earlyBirdCount, totalTime, flashcardsCount, certificates, xpBoostEndTime]);

  // Herzen regenerieren (alle 5 Stunden)
  useEffect(() => {
    if (hearts < 5) {
      const checkRegeneration = () => {
        const now = Date.now();
        const msPerHeart = 5 * 60 * 60 * 1000; // 5 Stunden
        const timeDiff = now - lastHeartUpdate;
        
        if (timeDiff >= msPerHeart) {
          const heartsToAdd = Math.floor(timeDiff / msPerHeart);
          const newHearts = Math.min(5, hearts + heartsToAdd);
          
          if (newHearts > hearts) {
            setHearts(newHearts);
            // Restzeit für das nächste Herz behalten
            const remainder = timeDiff % msPerHeart;
            setLastHeartUpdate(now - remainder);
          }
        }
      };
      const interval = setInterval(checkRegeneration, 1000); // Jede Sekunde prüfen
      return () => clearInterval(interval);
    }
  }, [hearts, lastHeartUpdate]);

  // Benachrichtigungen (lokal) checken
  useEffect(() => {
    if (!notificationsEnabled || !('Notification' in window) || Notification.permission !== 'granted') return;

    const checkAndNotify = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const todayStr = now.toDateString();

      const showNotification = (title, options) => {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.ready.then(registration => {
            registration.showNotification(title, options);
          });
        } else {
          new Notification(title, options);
        }
      };

      // 14:00 Uhr Lern-Erinnerung
      if (hours === 14 && minutes === 0) {
        const last14 = localStorage.getItem('last_notif_14');
        if (last14 !== todayStr) {
          showNotification('Zeit zum Lernen!', {
            body: 'Hey! Deine nächste Lektion wartet auf dich.',
            icon: '/logo192.png',
            badge: '/logo192.png'
          });
          localStorage.setItem('last_notif_14', todayStr);
        }
      }

      // 18:00 Uhr Streak-Erinnerung (nur wenn noch nicht gelernt)
      if (hours === 18 && minutes === 0) {
        const last18 = localStorage.getItem('last_notif_18');
        if (last18 !== todayStr) {
          if (lastLessonDate !== todayStr) {
            showNotification('Rette deinen Streak! 🔥', {
              body: 'Du hast heute noch keine Lektion gemacht. Verliere nicht deinen Streak!',
              icon: '/logo192.png',
              badge: '/logo192.png'
            });
          }
          localStorage.setItem('last_notif_18', todayStr);
        }
      }
    };

    // Check jede Minute (60.000ms)
    const interval = setInterval(checkAndNotify, 60000);
    checkAndNotify(); // Einmalig sofort beim Laden checken

    return () => clearInterval(interval);
  }, [notificationsEnabled, lastLessonDate]);

  // Klick-Wellen (Ripple Effect)
  useEffect(() => {
    const handleClick = (e) => {
      const newRipple = { x: e.clientX, y: e.clientY, id: Date.now() };
      setRipples((prev) => [...prev, newRipple]);
      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
      }, 800);
    };

    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  // Hintergrundfarbe zurücksetzen, wenn nicht im Lernen-Tab
  useEffect(() => {
    if (activeTab !== 'Lernen') {
      setBackgroundColor('#1cb0f6');
    }
  }, [activeTab]);

  const triggerAchievement = (id) => {
    if (!achievements.includes(id)) {
      const achievement = ALL_ACHIEVEMENTS.find(a => a.id === id);
      if (achievement) {
        setAchievements(prev => [...prev, id]);
        setCurrentAchievement(achievement);
        setShowAchievementModal(true);
        const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2000/2000-preview.m4a');
        audio.volume = 0.5;
        audio.play().catch(e => {});
      }
    }
  };

  // Check achievements when stats change
  useEffect(() => {
    if (completedLessons.length > 0) triggerAchievement('first_win');
    if (streak >= 3) triggerAchievement('streak_3');
    if (xp >= 1000) triggerAchievement('xp_1000');
  }, [completedLessons, streak, xp, achievements]);

  const handleStartLesson = (id) => {
    const startSound = new Audio('https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.m4a');
    startSound.volume = 0.5;
    startSound.play().catch(e => console.log("Audio play failed", e));
    setActiveLesson(id);
  };

  const handleStartPractice = () => {
    // Filter completed lessons for current course
    const courseLessons = completedLessons.filter(l => typeof l === 'string' && l.startsWith(activeCourse + '_'));
    
    // Get last 5
    const last5 = courseLessons.slice(-5);
    
    if (last5.length === 0) {
      alert("Du musst erst ein paar Lektionen abschließen, um zu üben!");
      return;
    }

    let allQuestions = [];
    last5.forEach(id => {
      if (lessonsData[id]) {
        allQuestions = [...allQuestions, ...lessonsData[id]];
      }
    });

    // Filter out 'info' slides
    const exercises = allQuestions.filter(q => q.type !== 'info');

    // Shuffle and pick 10
    const selected = exercises.sort(() => 0.5 - Math.random()).slice(0, 10);

    setPracticeContent(selected);
    setActiveLesson('practice');
  };

  const handleCollectLootbox = (level) => {
    if (!collectedLootboxes.includes(level)) {
      setGems(prev => prev + 500);
      setCollectedLootboxes(prev => [...prev, level]);
      
      // Lootbox als "abgeschlossen" markieren, damit die nächste Lektion freigeschaltet wird
      const lootboxId = typeof level === 'number' ? `${activeCourse}_${level}` : level;
      setCompletedLessons(prev => {
        if (!prev.includes(lootboxId)) {
          return [...prev, lootboxId];
        }
        return prev;
      });
      
      setShowLootboxModal(true);

      // Check für Achievements und Zertifikate auch bei Lootboxen durchführen
      if (typeof lootboxId === 'string' && lootboxId.match(/_\d+$/)) {
          const lessonNum = parseInt(lootboxId.split('_')[1]);
          const courseLimits = { 'Python': 40, 'JavaScript': 20, 'HTML': 80, 'Kotlin': 10 }; // JavaScript endet bei 20 (bzw. 80 wenn voll)
          
          // Zertifikat Check
          if (lootboxId === `${activeCourse}_${courseLimits[activeCourse]}`) {
             if (!certificates.includes(activeCourse)) {
                 setCertificates(prev => [...prev, activeCourse]);
                 setEarnedCertificate(activeCourse);
                 setShowCertificateModal(true);
             }
          }
          if (lessonNum % 10 === 0) triggerAchievement('graduate');
      }
    }
  };

  const handleCompleteLesson = (stats = { mistakes: 0, duration: 0, maxCombo: 0 }) => {
    if (activeLesson === 'practice') {
      setHearts(prev => Math.min(5, prev + 1));
      setPracticeContent(null);
      setActiveLesson(null);
      // Optional: Sound oder Alert für Herz-Gewinn
      return;
    }

    if (activeLesson) {
      const isXpBoostActive = xpBoostEndTime && xpBoostEndTime > Date.now();
      const earnedXp = isXpBoostActive ? 200 : 100;

      setCompletedLessons((prev) => [...prev, activeLesson]);
      setXp((prev) => prev + earnedXp);
      setGems((prev) => prev + 20); // Gems Belohnung
      setTotalTime((prev) => prev + stats.duration);

      // Missionen aktualisieren
      let gemsReward = 0;
      const newMissions = dailyMissions.map(m => {
          if (m.completed) return m;
          let p = m.progress;
          if (m.type === 'xp') p += earnedXp;
          if (m.type === 'minutes') p += Math.ceil(stats.duration / 60);
          if (m.type === 'lessons') p += 1;
          if (m.type === 'perfect' && stats.mistakes === 0) p += 1;
          if (m.type === 'combo' && stats.maxCombo >= m.target) p = m.target; // Sofort fertig bei Combo
          if (m.type === 'speed' && stats.duration < 120) p += 1;
          
          const hour = new Date().getHours();
          if (m.type === 'early_bird' && hour < 12) p += 1;
          if (m.type === 'night_owl' && hour >= 20) p += 1;
          
          if (p >= m.target) {
              gemsReward += 100;
              return { ...m, progress: p, completed: true };
          }
          return { ...m, progress: p };
      });
      setDailyMissions(newMissions);
      if (gemsReward > 0) setGems(prev => prev + gemsReward);

      // Streak Logik
      const today = new Date();
      today.setHours(0,0,0,0);
      const todayStr = today.toDateString();
      
      if (lastLessonDate !== todayStr) {
        let tempFreezes = streakFreezes;
        let tempUsedDates = [...freezeUsedDates];
        let tempStreak = streak;
        let tempLastDate = lastLessonDate ? new Date(lastLessonDate) : null;
        
        if (tempLastDate) {
           tempLastDate.setHours(0,0,0,0);
           let yesterday = new Date(today);
           yesterday.setDate(yesterday.getDate() - 1);
           
           while (tempLastDate < yesterday && tempStreak > 0) {
              tempLastDate.setDate(tempLastDate.getDate() + 1);
              if (tempFreezes > 0) {
                 tempFreezes--;
                 tempUsedDates.push(tempLastDate.toDateString());
              } else {
                 tempStreak = 0;
                 break;
              }
           }
        }
        
        if (tempLastDate && tempLastDate.getTime() === (new Date(today.getTime() - 86400000)).getTime() && tempStreak > 0) {
            setStreak(tempStreak + 1);
        } else {
            setStreak(1);
        }
        
        setStreakFreezes(tempFreezes);
        setFreezeUsedDates(tempUsedDates);
        setLastLessonDate(todayStr);
      }

      // Achievement Checks
      if (stats.mistakes === 0) {
        setPerfectLessonCount(prev => prev + 1);
        triggerAchievement('perfect');
      }
      
      const hour = new Date().getHours();
      if (hour >= 22) {
        setNightOwlCount(prev => prev + 1);
        if (nightOwlCount + 1 >= 3) triggerAchievement('night_owl');
      }
      if (hour < 8) {
        setEarlyBirdCount(prev => prev + 1);
        if (earlyBirdCount + 1 >= 3) triggerAchievement('early_bird');
      }
      
      if (totalTime + stats.duration >= 3600) triggerAchievement('marathon'); // 1h
      
      const day = new Date().getDay();
      if (day === 0) { // Sunday check
         const yesterday = new Date();
         yesterday.setDate(yesterday.getDate() - 1);
         if (lastLessonDate === yesterday.toDateString()) triggerAchievement('weekend_warrior');
      }
      
      if (hearts === 1) triggerAchievement('survivor');
      if (stats.duration < 90) triggerAchievement('speed_coder');
      
      if (typeof activeLesson === 'string' && activeLesson.match(/_\d+$/)) {
          const lessonNum = parseInt(activeLesson.split('_')[1]);
          const courseLimits = { 'Python': 40, 'JavaScript': 20, 'HTML': 80, 'Kotlin': 10 };
          
          // Zertifikat Check
          if (activeLesson === `${activeCourse}_${courseLimits[activeCourse]}`) {
             if (!certificates.includes(activeCourse)) {
                 setCertificates(prev => [...prev, activeCourse]);
                 setEarnedCertificate(activeCourse);
                 setShowCertificateModal(true);
             }
          }
          if (lessonNum % 10 === 0) triggerAchievement('graduate');
      }

      setActiveLesson(null);
    }
  };

  const handleFlashcardReview = () => {
    let gemsReward = 0;
    const newMissions = dailyMissions.map(m => {
        if (m.completed) return m;
        let p = m.progress;
        if (m.type === 'flashcards') p += 1;
        
        if (p >= m.target) {
            gemsReward += 100;
            return { ...m, progress: p, completed: true };
        }
        return { ...m, progress: p };
    });
    if (JSON.stringify(newMissions) !== JSON.stringify(dailyMissions)) {
        setDailyMissions(newMissions);
        if (gemsReward > 0) setGems(prev => prev + gemsReward);
    }
    
    setFlashcardsCount(prev => prev + 1);
    if (flashcardsCount + 1 >= 10) triggerAchievement('memory_pro');
  };

  const handlePlaygroundRun = () => {
    let gemsReward = 0;
    const newMissions = dailyMissions.map(m => {
        if (m.completed) return m;
        let p = m.progress;
        if (m.type === 'playground') p += 1;
        
        if (p >= m.target) {
            gemsReward += 100;
            return { ...m, progress: p, completed: true };
        }
        return { ...m, progress: p };
    });
    if (JSON.stringify(newMissions) !== JSON.stringify(dailyMissions)) {
        setDailyMissions(newMissions);
        if (gemsReward > 0) setGems(prev => prev + gemsReward);
    }
  };

  const reduceHearts = () => {
    if (hearts === 5) {
      setLastHeartUpdate(Date.now());
    }
    setHearts((prev) => Math.max(0, prev - 1));
  };

  const handleRefillHearts = () => {
    if (gems >= 350) {
      setGems(prev => prev - 350);
      setHearts(5);
      setLastHeartUpdate(Date.now());
    }
  };

  const handleCloseLesson = () => {
    setActiveLesson(null);
  };

  const toggleNotifications = async () => {
    if (!notificationsEnabled) {
      if (!('Notification' in window)) {
        alert('Dein Browser unterstützt leider keine Benachrichtigungen.');
        return;
      }
      const permission = await Notification.requestPermission();
      localStorage.setItem('duocode_notifications_requested', 'true');
      if (permission === 'granted') {
        setNotificationsEnabled(true);
      } else {
        alert('Bitte erlaube Benachrichtigungen in deinen Browser-Einstellungen, um diese Funktion zu nutzen.');
      }
    } else {
      setNotificationsEnabled(false);
    }
  };

  const handleStartClick = () => {
    if (user) {
      setStarted(true);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error("Login Fehler:", error);
      alert(`Login fehlgeschlagen:\nNachricht: ${error.message}\nFehler-Code: ${error.code}\nDomain: ${window.location.hostname}`);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setStarted(false);
    resetState();
  };

  const handleBuyItem = (price, itemId) => {
    if (itemId === 'ad_heart') {
      setShowAdModal(true);
      return;
    }
    if (itemId === 'heart') {
      if (hearts === 5) {
        alert("Du hast bereits volle Herzen!");
        return;
      }
      setGems(prev => prev - price);
      setHearts(5);
      setLastHeartUpdate(Date.now());
    } else if (itemId === 'freeze') {
      if (streakFreezes >= 5) {
        alert("Du hast bereits die maximale Anzahl an Streak Freezes!");
        return;
      }
      setGems(prev => prev - price);
      setStreakFreezes(prev => prev + 1);
    } else if (itemId === 'xpboost') {
      if (xpBoostEndTime && xpBoostEndTime > Date.now()) {
        alert("Dein Doppel-XP Boost ist bereits aktiv!");
        return;
      }
      setGems(prev => prev - price);
      setXpBoostEndTime(Date.now() + 15 * 60 * 1000);
    } else {
      setGems(prev => prev - price);
      alert(`Du hast ${itemId} gekauft!`);
    }
  };

  const downloadCertificate = async (courseName) => {
    const userName = user ? (user.displayName || user.email) : "Gast";
    
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', format: 'a4' });
    
    const fontName = "helvetica";
    
    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();

    // Farben definieren
    const blue = '#1cb0f6';
    const green = '#58cc02';
    const yellow = '#ffc800';
    const pink = '#FF86D0';
    const darkText = '#3c3c3c';
    const grayText = '#777777';

    // Hintergrund (Weiß)
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, width, height, 'F');

    // Verspieltes Design (Kreise in den Ecken)
    doc.setFillColor(blue);
    doc.circle(0, 0, 60, 'F'); // Oben Links

    doc.setFillColor(green);
    doc.circle(width, height, 50, 'F'); // Unten Rechts

    doc.setFillColor(yellow);
    doc.circle(width * 0.15, height * 0.85, 10, 'F'); // Akzent unten links
    
    doc.setFillColor(pink);
    doc.circle(width * 0.85, height * 0.15, 8, 'F'); // Akzent oben rechts
    
    // Logo / Brand
    doc.setTextColor(255, 255, 255);
    doc.setFont(fontName, "bold");
    doc.setFontSize(24);
    doc.text("duocode", 10, 25);
    
    // Title
    doc.setTextColor(darkText);
    doc.setFontSize(40);
    doc.text("ZERTIFIKAT", width / 2, 70, { align: "center" });
    
    // Subtitle
    doc.setFont(fontName, "normal");
    doc.setFontSize(16);
    doc.setTextColor(grayText);
    doc.text("Hiermit wird bestätigt, dass", width / 2, 90, { align: "center" });
    
    // Name
    doc.setFont(fontName, "bold");
    doc.setFontSize(32);
    doc.setTextColor(blue);
    doc.text(userName, width / 2, 110, { align: "center" });
    
    // Trennlinie
    doc.setDrawColor(229, 229, 229);
    doc.setLineWidth(1);
    doc.line(width / 2 - 50, 120, width / 2 + 50, 120);
    
    // Kurs Text
    doc.setFontSize(20);
    doc.setFont(fontName, "normal");
    doc.setTextColor(grayText);
    doc.text(`den Kurs erfolgreich abgeschlossen hat:`, width / 2, 140, { align: "center" });
    
    // Logo laden und anzeigen
    const logoUrls = {
        'Python': "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg",
        'JavaScript': "https://raw.githubusercontent.com/devicons/devicon/master/icons/javascript/javascript-original.svg",
        'HTML': "https://raw.githubusercontent.com/devicons/devicon/master/icons/html5/html5-original.svg",
        'Kotlin': "https://raw.githubusercontent.com/devicons/devicon/master/icons/kotlin/kotlin-original.svg"
    };

    const logoUrl = logoUrls[courseName];
    if (logoUrl) {
        try {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.src = logoUrl;
            await new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve; // Continue even if failed
            });
            
            // Canvas erstellen um SVG zu PNG zu konvertieren für jsPDF
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const imgData = canvas.toDataURL('image/png');
            
            const logoSize = 50;
            doc.addImage(imgData, 'PNG', (width - logoSize) / 2, 155, logoSize, logoSize);
        } catch (e) {
            console.error("Logo konnte nicht geladen werden", e);
        }
    }

    // Kurs Name
    doc.setFont(fontName, "bold");
    doc.setFontSize(28);
    doc.setTextColor(darkText);
    doc.text(courseName, width / 2, 225, { align: "center" });
    
    // Datum & Unterschrift
    doc.setFont(fontName, "normal");
    doc.setFontSize(12);
    doc.setTextColor(grayText);
    const dateStr = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
    doc.text(`Ausgestellt am ${dateStr}`, width / 2, 260, { align: "center" });
    
    doc.setFont(fontName, "italic");
    doc.text("Das duocode Team", width / 2, 270, { align: "center" });

    doc.save(`Zertifikat_${courseName}.pdf`);
  };

  if (!started) {
    if (activeTab === 'Datenschutz' || activeTab === 'Impressum') {
      return (
        <div className={`app ${darkMode ? 'dark-mode' : ''} landing-page`} style={{ backgroundColor: darkMode ? '#131313' : 'transparent', color: darkMode ? '#fff' : 'inherit', minHeight: '100vh', position: 'relative' }}>
          <style>{`
            body, button, input, select, textarea {
              font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
              font-weight: 700;
            }
            .main-content::-webkit-scrollbar, .scroll-container::-webkit-scrollbar {
              display: none;
            }
            .main-content, .scroll-container {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .public-layout .left-sidebar,
            .public-layout .right-sidebar {
              visibility: hidden !important;
              pointer-events: none !important;
            }
            @media (max-width: 768px) {
              .public-layout .left-sidebar,
              .public-layout .right-sidebar {
                display: none !important;
              }
            }
            .landing-page, .main-layout, .app, .main-content {
              animation: none !important;
            }
          `}</style>
          <div className="floating-shapes">
            <span className="shape shape-1">{"{ }"}</span>
            <span className="shape shape-2">{"</>"}</span>
            <span className="shape shape-3">{";"}</span>
            <span className="shape shape-4">{"()"}</span>
            <span className="shape shape-5">{"#"}</span>
          </div>
          <header className="landing-header" style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 10, pointerEvents: 'none' }}>
            <a href="/" onClick={(e) => { e.preventDefault(); setActiveTab('Lernen'); window.history.pushState({}, '', '/'); }} style={{ textDecoration: 'none', pointerEvents: 'auto', display: 'inline-block' }}>
              <h1 className="duocode-logo">duocode</h1>
            </a>
          </header>
          <div className="main-layout public-layout" style={{ position: 'relative', zIndex: 1 }}>
            <LeftSidebar activeTab={activeTab} onTabChange={() => {}} user={null} onLogout={() => {}} darkMode={darkMode} toggleDarkMode={() => {}} notificationsEnabled={false} toggleNotifications={() => {}} />
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
              {activeTab === 'Datenschutz' && <Privacy darkMode={darkMode} />}
              {activeTab === 'Impressum' && <Impressum darkMode={darkMode} />}
            </div>
            <RightSidebar activeCourse="Python" hearts={5} streak={0} darkMode={darkMode} gems={0} lastHeartUpdate={Date.now()} onRefillHearts={() => {}} onStartPractice={() => {}} onShowMissions={() => {}} onShowAbout={() => {}} onShowImpressum={() => {}} onShowPrivacy={() => {}} onShowCourses={() => {}} completedLessons={[]} onSelectCourse={() => {}} dailyMissions={[]} onShowAdModal={() => {}} freezeUsedDates={[]} />
          </div>
        </div>
      );
    }

    return (
      <div style={{ minHeight: '100vh', backgroundColor: darkMode ? '#131313' : 'transparent' }}>
        <style>{`
          body, button, input, select, textarea { font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif; }
          .landing-page { animation: none !important; }
        `}</style>
        <LandingPage onStart={handleStartClick} darkMode={darkMode} onNavigate={(tab) => setActiveTab(tab)} />
        <InstallPWA darkMode={darkMode} user={user} />
        {showLoginModal && (
          <div className="modal-overlay">
            <div className="modal-content login-modal" style={{
              backgroundColor: '#ffffff',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}>
              <div className="login-icon" style={{
                backgroundColor: '#ddf4ff',
                borderRadius: '24px',
                padding: '20px',
                display: 'inline-flex',
                marginBottom: '20px'
              }}>
                <LogIn size={48} color="#1cb0f6" strokeWidth={3} />
              </div>
              <p>Melde dich an, um deinen Fortschritt nicht zu verlieren.</p>
              <button className="google-login-button" onClick={() => { if (navigator.vibrate) navigator.vibrate(10); handleGoogleLogin(); }}>
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
                Mit Google anmelden
              </button>
              <button className="text-button" onClick={() => { if (navigator.vibrate) navigator.vibrate(10); setShowLoginModal(false); }}>Abbrechen</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`app ${darkMode ? 'dark-mode' : ''}`} style={{ backgroundColor: darkMode ? '#131313' : '#ffffff' }}>
      <style>{`
        .ripple {
          position: absolute;
          width: 10px;
          height: 10px;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          animation: ripple-anim 0.6s linear forwards;
        }
        @keyframes ripple-anim {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
          100% { transform: translate(-50%, -50%) scale(10); opacity: 0; }
        }
        .main-content, .lesson-full-screen, .main-layout {
          animation: none !important;
        }

        /* Global bold text */
        body, button, input, select, textarea {
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
          font-weight: 700;
        }

        /* Exception for lesson answers */
        .option-card {
          font-weight: 400;
        }

        .mobile-only-menu-item {
          display: none;
        }

        /* Scrollbar global ausblenden */
        .main-content::-webkit-scrollbar, .scroll-container::-webkit-scrollbar {
          display: none;
        }
        .main-content, .scroll-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        /* Mobile View Styles */
        @media (max-width: 768px) {
          .mobile-only-menu-item {
            display: block !important;
          }

          html, body, #root, .app {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
            overflow: hidden;
          }

          .main-layout {
            display: block !important;
            width: 100% !important;
            height: 100% !important;
            position: relative !important;
            overflow: hidden !important;
          }
          
          /* Left Sidebar (Bottom Nav) */
          .left-sidebar {
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            top: auto !important;
            width: 100% !important;
            height: 80px !important;
            border-right: none !important;
            border-top: 2px solid ${darkMode ? '#333' : '#e5e5e5'} !important;
            display: flex !important;
            flex-direction: row !important;
            justify-content: center !important;
            padding: 0 !important;
            z-index: 2000 !important;
            background-color: ${darkMode ? 'rgba(19, 19, 19, 0.98)' : 'rgba(255, 255, 255, 0.98)'} !important;
            backdrop-filter: blur(10px) !important;
            -webkit-backdrop-filter: blur(10px) !important;
            overflow: visible !important;
          }
          
          .left-sidebar .logo-container {
            display: none !important;
          }
          
          .left-sidebar ul {
            display: flex !important;
            flex-direction: row !important;
            width: 100% !important;
            justify-content: space-around !important;
            padding: 0 !important;
            margin: 0 !important;
            list-style: none !important;
          }
          
          .left-sidebar li {
            height: 100% !important;
            padding: 0 !important;
            flex: 1 !important;
            justify-content: center !important;
            border-radius: 0 !important;
          }
          
          .left-sidebar li.active {
            background-color: transparent !important;
            border: none !important;
          }
          
          .left-sidebar li > span {
            display: none !important;
          }

          .left-sidebar .menu-icon {
            margin-right: 0 !important;
          }

          .left-sidebar li.active .menu-icon {
            background-color: ${darkMode ? 'rgba(28, 176, 246, 0.2)' : '#ddf4ff'} !important;
            border: 2px solid #1cb0f6 !important;
            border-radius: 16px !important;
            width: 52px !important;
            height: 52px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          .menu-item-shop {
            display: none !important;
          }
          
          /* Right Sidebar (Top Bar) */
          .right-sidebar {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            right: 0 !important;
            height: auto !important;
            min-height: 70px !important;
            padding: 10px 15px !important;
            z-index: 2000 !important;
            background-color: ${darkMode ? 'rgba(19, 19, 19, 0.98)' : 'rgba(255, 255, 255, 0.98)'} !important;
            backdrop-filter: blur(10px) !important;
            -webkit-backdrop-filter: blur(10px) !important;
            border-left: none !important;
            border-bottom: 2px solid ${darkMode ? '#333' : '#e5e5e5'} !important;
            box-sizing: border-box !important;
            display: flex !important;
            align-items: center !important;
            flex-direction: row !important;
          }
          
          .right-sidebar .stats-bar {
            margin-bottom: 0 !important;
            justify-content: space-between !important;
            width: 100% !important;
          }
          
          /* Hide widgets in right sidebar */
          .right-sidebar .widget-card,
          .right-sidebar > div:last-child {
            display: none !important;
          }
          
          /* Main Content Area */
          .main-layout > div:nth-child(2) {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            padding-top: 70px !important; /* Platz für Header */
            padding-bottom: 80px !important; /* Platz für Footer */
            margin: 0 !important;
            box-sizing: border-box !important;
            max-width: none !important;
            overflow: hidden !important;
          }

          /* Ensure inner content fits */
          .main-content {
            height: 100% !important;
          }

          .scroll-container {
            padding: 10px !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Adjust popups for mobile */
          .stat-item > div[style*="position: absolute"] {
            position: fixed !important;
            top: 75px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            width: 90% !important;
            max-width: 350px !important;
          }
          
          .more-popover {
            position: fixed !important;
            bottom: 85px !important;
            left: 0 !important;
            right: 0 !important;
            margin: 0 auto !important;
            width: 90% !important;
            max-width: 300px !important;
            transform-origin: bottom center !important;
          }
        }
      `}</style>

      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="ripple"
          style={{ left: ripple.x, top: ripple.y }}
        />
      ))}
      <InstallPWA 
        darkMode={darkMode} 
        user={user} 
        isOpen={showInstallPWAModal || showInstallPWAModalIOS} 
        onClose={() => { setShowInstallPWAModal(false); setShowInstallPWAModalIOS(false); }} 
        forceIOS={showInstallPWAModalIOS}
      />
      {showLootboxModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: darkMode ? '1px solid #333' : '1px solid rgba(255, 255, 255, 0.5)',
            padding: '40px',
            borderRadius: '24px',
            maxWidth: '420px'
          }}>
            <div style={{
              backgroundColor: darkMode ? 'rgba(255, 200, 0, 0.2)' : '#fff5d1',
              borderRadius: '24px',
              padding: '20px',
              display: 'inline-flex',
              marginBottom: '20px'
            }}>
              <Gift size={48} color="#ffc800" strokeWidth={3} />
            </div>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '10px', color: '#1cb0f6' }}>Belohnung!</h2>
            <p style={{ fontSize: '1.1rem', color: darkMode ? '#ccc' : '#777', marginBottom: '30px' }}>Du hast 500 Edelsteine erhalten.</p>
            <button 
              className="google-login-button" 
              onClick={() => { if (navigator.vibrate) navigator.vibrate(10); setShowLootboxModal(false); }}
              style={{ marginTop: 0, width: '100%', justifyContent: 'center' }}
            >
              Klasse!
            </button>
          </div>
        </div>
      )}
      {showNoHeartsModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center',
            backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: darkMode ? '1px solid #333' : '1px solid rgba(255, 255, 255, 0.5)',
            padding: '40px',
            borderRadius: '24px',
            maxWidth: '420px'
          }}>
            <div style={{
              backgroundColor: darkMode ? 'rgba(255, 75, 75, 0.2)' : '#ffdde0',
              borderRadius: '24px',
              padding: '20px',
              display: 'inline-flex',
              marginBottom: '20px'
            }}>
              <HeartCrack size={48} color="#ff4b4b" strokeWidth={3} />
            </div>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '10px' }}>Oh nein!</h2>
            <p style={{ fontSize: '1.1rem', color: darkMode ? '#ccc' : '#777', marginBottom: '30px' }}>Du hast keine Herzen mehr.</p>
            <button 
              className="google-login-button" 
              onClick={() => { if (navigator.vibrate) navigator.vibrate(10); setShowNoHeartsModal(false); }}
              style={{ marginTop: 0, width: '100%', justifyContent: 'center' }}
            >
              Schließen
            </button>
          </div>
        </div>
      )}
      {showAdModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: darkMode ? '1px solid #333' : '1px solid rgba(255, 255, 255, 0.5)',
            padding: '40px',
            borderRadius: '24px',
            maxWidth: '420px'
          }}>
            <div style={{
              backgroundColor: darkMode ? 'rgba(28, 176, 246, 0.2)' : '#ddf4ff',
              borderRadius: '24px',
              padding: '20px',
              display: 'inline-flex',
              marginBottom: '20px'
            }}>
              <Tv size={48} color="#1cb0f6" strokeWidth={3} />
            </div>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '10px', color: '#1cb0f6' }}>Bald verfügbar!</h2>
            <p style={{ fontSize: '1.1rem', color: darkMode ? '#ccc' : '#777', marginBottom: '30px' }}>Das Schauen von Werbung ist aktuell noch nicht verfügbar. Schau später wieder vorbei!</p>
            <button 
              className="google-login-button" 
              onClick={() => { if (navigator.vibrate) navigator.vibrate(10); setShowAdModal(false); }}
              style={{ marginTop: 0, width: '100%', justifyContent: 'center' }}
            >
              Okay
            </button>
          </div>
        </div>
      )}
      {showAchievementModal && currentAchievement && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: darkMode ? '1px solid #333' : '1px solid rgba(255, 255, 255, 0.5)',
            padding: '40px',
            borderRadius: '24px',
            maxWidth: '420px'
          }}>
            <div style={{
              backgroundColor: darkMode ? 'rgba(255, 200, 0, 0.2)' : '#fff5d1',
              borderRadius: '24px',
              padding: '20px',
              display: 'inline-flex',
              marginBottom: '20px'
            }}>
              <Trophy size={48} color="#ffc800" strokeWidth={3} />
            </div>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '10px', color: '#ffc800' }}>Errungenschaft!</h2>
            <div style={{ fontSize: '4rem', marginBottom: '10px' }}>{currentAchievement.icon}</div>
            <h3 style={{ fontSize: '1.4rem', margin: '0 0 10px 0', color: darkMode ? '#fff' : '#333' }}>{currentAchievement.name}</h3>
            <p style={{ fontSize: '1.1rem', color: darkMode ? '#ccc' : '#777', marginBottom: '30px' }}>{currentAchievement.desc}</p>
            <button 
              className="google-login-button" 
              onClick={() => { if (navigator.vibrate) navigator.vibrate(10); setShowAchievementModal(false); }}
              style={{ marginTop: 0, width: '100%', justifyContent: 'center' }}
            >
              Klasse!
            </button>
          </div>
        </div>
      )}
      {/* Certificate Popup */}
      {showCertificateModal && earnedCertificate && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: darkMode ? '1px solid #333' : '1px solid rgba(255, 255, 255, 0.5)',
            padding: '40px',
            borderRadius: '24px',
            maxWidth: '420px'
          }}>
            <div style={{
              backgroundColor: darkMode ? 'rgba(255, 200, 0, 0.2)' : '#fff5d1',
              borderRadius: '24px',
              padding: '20px',
              display: 'inline-flex',
              marginBottom: '20px'
            }}>
              <Award size={64} color="#ffc800" strokeWidth={2} />
            </div>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '10px', color: '#ffc800' }}>Herzlichen Glückwunsch!</h2>
            <h3 style={{ fontSize: '1.2rem', margin: '0 0 10px 0', color: darkMode ? '#fff' : '#333' }}>Kurs {earnedCertificate} abgeschlossen</h3>
            <p style={{ fontSize: '1.1rem', color: darkMode ? '#ccc' : '#777', marginBottom: '30px' }}>Du hast den Kurs erfolgreich gemeistert. Hier ist dein Zertifikat!</p>
            <button className="google-login-button" onClick={() => { if (navigator.vibrate) navigator.vibrate(10); downloadCertificate(earnedCertificate); }} style={{ marginTop: 0, width: '100%', justifyContent: 'center', marginBottom: '10px' }}>
              <Download size={20} style={{marginRight: '8px'}}/> Zertifikat herunterladen
            </button>
            <button className="text-button" onClick={() => setShowCertificateModal(false)}>Schließen</button>
          </div>
        </div>
      )}

      {/* Course Discovery Popup */}
      {showCourseDiscoveryModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: darkMode ? '1px solid #333' : '1px solid rgba(255, 255, 255, 0.5)',
            padding: '40px',
            borderRadius: '24px',
            maxWidth: '420px'
          }}>
            <style>{`
              @keyframes logo-cycle {
                0% { opacity: 0; transform: translate(-50%, -50%) scale(0); }
                3% { opacity: 1; transform: translate(-50%, -50%) scale(1.15); }
                6% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                16% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                18% { opacity: 1; transform: translate(-50%, -50%) scale(1.15); }
                20% { opacity: 0; transform: translate(-50%, -50%) scale(0); }
                100% { opacity: 0; transform: translate(-50%, -50%) scale(0); }
              }
            `}</style>
            <div style={{
              backgroundColor: darkMode ? 'rgba(28, 176, 246, 0.2)' : '#ddf4ff',
              borderRadius: '50%',
              width: '88px',
              height: '88px',
              margin: '0 auto 20px auto',
              position: 'relative'
            }}>
              {[
                "https://raw.githubusercontent.com/devicons/devicon/master/icons/javascript/javascript-original.svg",
                "https://raw.githubusercontent.com/devicons/devicon/master/icons/html5/html5-original.svg",
                "https://raw.githubusercontent.com/devicons/devicon/master/icons/java/java-original.svg",
                "https://raw.githubusercontent.com/devicons/devicon/master/icons/cplusplus/cplusplus-original.svg",
                "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original.svg"
              ].map((src, i) => (
                <img key={i} src={src} alt="logo" style={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  width: '48px', height: '48px',
                  opacity: 0,
                  transform: 'translate(-50%, -50%) scale(0)',
                  animation: 'logo-cycle 10s infinite',
                  animationDelay: `${i * 2}s`
                }} />
              ))}
            </div>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '10px', color: '#1cb0f6' }}>Wusstest du schon?</h2>
            <p style={{ fontSize: '1.1rem', color: darkMode ? '#ccc' : '#777', marginBottom: '30px', lineHeight: '1.5' }}>
              Du kannst auf Duocode auch andere Programmiersprachen wie JavaScript oder HTML lernen!<br/><br/>
              Klicke einfach oben auf das aktuelle Kurs-Symbol und wähle <strong>"Neuen Kurs hinzufügen"</strong> aus.
            </p>
            <button className="google-login-button" onClick={() => { if (navigator.vibrate) navigator.vibrate(10); setShowCourseDiscoveryModal(false); }} style={{ marginTop: 0, width: '100%', justifyContent: 'center' }}>
              Klasse!
            </button>
          </div>
        </div>
      )}

      {activeLesson ? (
        <Lesson 
          lessonId={activeLesson} 
          onComplete={handleCompleteLesson} 
          onClose={handleCloseLesson} 
          hearts={hearts}
          reduceHearts={reduceHearts}
          currentStreak={streak}
          lastLessonDate={lastLessonDate}
          darkMode={darkMode}
          customLessonContent={activeLesson === 'practice' ? practiceContent : null}
          freezeUsedDates={freezeUsedDates}
          activeCourse={activeCourse}
          xpBoostActive={xpBoostEndTime && xpBoostEndTime > Date.now()}
        />
      ) : (
        <div className="main-layout">
          <LeftSidebar activeTab={activeTab} onTabChange={setActiveTab} user={user} onLogout={handleLogout} darkMode={darkMode} toggleDarkMode={() => setDarkMode(!darkMode)} notificationsEnabled={notificationsEnabled} toggleNotifications={toggleNotifications} />
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            {!activeLesson && <BackgroundAnimation color={backgroundColor} />}
            {activeTab === 'Admin' && user && user.email === 'schwingnorbert@gmail.com' && <AdminDashboard 
              onAddHearts={() => setHearts(prev => prev + 5)}
              onRemoveHearts={() => setHearts(prev => Math.max(0, prev - 5))}
              onShowStreak={() => setActiveLesson('debug-streak')}
              onShowSummary={() => setActiveLesson('debug-summary')}
              onShowNoHearts={() => setShowNoHeartsModal(true)}
              onShowLootbox={() => setShowLootboxModal(true)}
              onShowInstallPWA={() => setShowInstallPWAModal(true)}
              onShowInstallPWAIOS={() => setShowInstallPWAModalIOS(true)}
              onShowTheory={() => setActiveLesson('debug-theory')}
              onShowAchievement={() => { setCurrentAchievement(ALL_ACHIEVEMENTS[0]); setShowAchievementModal(true); }}
              onShowCertificateModal={(course) => { setEarnedCertificate(course); setShowCertificateModal(true); }}
              onDownloadCertificate={downloadCertificate}
              onShowCourseDiscovery={() => setShowCourseDiscoveryModal(true)}
            />}
            {activeTab === 'Lernen' && <MainContent activeCourse={activeCourse} completedLessons={completedLessons} onStartLesson={handleStartLesson} collectedLootboxes={collectedLootboxes} onCollectLootbox={handleCollectLootbox} onSectionColorChange={setBackgroundColor} />}
            {activeTab === 'Profil' && <Profile xp={xp} user={user} darkMode={darkMode} setDarkMode={setDarkMode} achievements={achievements} streak={streak} gems={gems} perfectLessonCount={perfectLessonCount} nightOwlCount={nightOwlCount} earlyBirdCount={earlyBirdCount} totalTime={totalTime} flashcardsCount={flashcardsCount} certificates={certificates} onDownloadCertificate={downloadCertificate} />}
            {activeTab === 'Shop' && <Shop gems={gems} buyItem={handleBuyItem} darkMode={darkMode} streakFreezes={streakFreezes} xpBoostEndTime={xpBoostEndTime} />}
            {activeTab === 'Playground' && <Playground activeCourse={activeCourse} onRunCode={handlePlaygroundRun} />}
            {activeTab === 'Karteikarten' && <Flashcards darkMode={darkMode} user={user} activeCourse={activeCourse} onCardReviewed={handleFlashcardReview} />}
            {activeTab === 'About' && <About darkMode={darkMode} />}
            {activeTab === 'Kurse' && <Courses darkMode={darkMode} activeCourse={activeCourse} completedLessons={completedLessons} onSelectCourse={(name) => { setActiveCourse(name); setActiveTab('Lernen'); }} />}
            {activeTab === 'Impressum' && <Impressum darkMode={darkMode} />}
            {activeTab === 'Datenschutz' && <Privacy darkMode={darkMode} />}
            {activeTab === 'Missionen' && <Missions dailyMissions={dailyMissions} darkMode={darkMode} />}
          </div>
          <RightSidebar activeCourse={activeCourse} hearts={hearts} streak={streak} darkMode={darkMode} gems={gems} lastHeartUpdate={lastHeartUpdate} onRefillHearts={handleRefillHearts} onStartPractice={handleStartPractice} onShowMissions={() => setActiveTab('Missionen')} onShowAbout={() => setActiveTab('About')} onShowImpressum={() => setActiveTab('Impressum')} onShowPrivacy={() => setActiveTab('Datenschutz')} onShowCourses={() => setActiveTab('Kurse')} completedLessons={completedLessons} onSelectCourse={(name) => { setActiveCourse(name); setActiveTab('Lernen'); }} dailyMissions={dailyMissions} onShowAdModal={() => setShowAdModal(true)} freezeUsedDates={freezeUsedDates} />
        </div>
      )}
    </div>
  );
}

export default App;
